import React, { useEffect, useRef } from 'react'
import { TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PasswordStrengthIndicator } from '@/design-system/molecules/PasswordStrengthIndicator'
import { EmailValidationIndicator } from '@/design-system/atoms/EmailValidationIndicator'
import { validateEmail } from '@/lib/utils/emailValidation'
import { validateUsername } from '@/app/validation/usernameRules'
import { PasswordTextField } from './PasswordTextField'

interface RegisterFormFieldsProps {
    email: string
    username: string
    password: string
    confirmPassword: string
    inviteKey?: string
    inviteKeyRequired?: boolean
    inviteKeyError?: string | null
    showPassword: boolean
    showConfirmPassword: boolean
    touchedFields: Record<'username' | 'email' | 'password' | 'confirmPassword', boolean>
    onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onInviteKeyChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onTogglePasswordVisibility: () => void
    onToggleConfirmPasswordVisibility: () => void
}

export const RegisterFormFields: React.FC<RegisterFormFieldsProps> = ({
    email,
    username,
    password,
    confirmPassword,
    inviteKey,
    inviteKeyRequired = false,
    inviteKeyError,
    showPassword,
    showConfirmPassword,
    touchedFields,
    onEmailChange,
    onUsernameChange,
    onPasswordChange,
    onConfirmPasswordChange,
    onInviteKeyChange,
    onTogglePasswordVisibility,
    onToggleConfirmPasswordVisibility,
}) => {
    const { t } = useTranslation()

    const uv = validateUsername(username)
    const usernameHasError = touchedFields.username && !uv.ok
    const emailHasError = touchedFields.email && !validateEmail(email).isValid
    const inviteKeyHasError = Boolean(inviteKeyError)

    // Pull focus to the invite-key field when an error appears so the browser
    // scrolls it into view — the field is near the bottom of the form and would
    // otherwise be missed.
    const inviteKeyInputRef = useRef<HTMLInputElement | null>(null)
    useEffect(() => {
        if (inviteKeyHasError) {
            inviteKeyInputRef.current?.focus()
        }
    }, [inviteKeyHasError])

    return (
        <>
            <TextField
                margin="normal"
                required
                fullWidth
                variant="outlined"
                id="email"
                label={t('auth:email')}
                name="email"
                autoComplete="email"
                type="email"
                autoFocus
                value={email}
                onChange={onEmailChange}
                error={emailHasError}
                aria-invalid={emailHasError}
                slotProps={{
                    htmlInput: {
                        'aria-describedby': emailHasError ? 'email-validation-error' : undefined,
                    },
                }}
            />
            <EmailValidationIndicator
                email={email}
                touched={touchedFields.email}
                id="email-validation-error"
            />
            <TextField
                margin="normal"
                required
                fullWidth
                variant="outlined"
                id="username"
                label={t('auth:username')}
                name="username"
                autoComplete="username"
                value={username}
                onChange={onUsernameChange}
                error={usernameHasError}
                aria-invalid={usernameHasError}
                helperText={t(
                    !usernameHasError
                        ? 'auth:usernameHelper'
                        : `auth:errors.username${uv.reason === 'length' ? 'Length' : 'Chars'}`
                )}
                slotProps={{
                    htmlInput: {
                        maxLength: 20,
                        pattern: '[\\p{L}\\p{N}_-]+',
                        'aria-describedby': usernameHasError
                            ? 'username-validation-error'
                            : undefined,
                    },

                    formHelperText: {
                        id: usernameHasError ? 'username-validation-error' : undefined,
                    },
                }}
            />
            <PasswordTextField
                name="password"
                label={t('auth:password')}
                value={password}
                showPassword={showPassword}
                onChange={onPasswordChange}
                onToggleVisibility={onTogglePasswordVisibility}
                showLabel={t('auth:showPassword')}
                hideLabel={t('auth:hidePassword')}
            />
            <PasswordStrengthIndicator password={password} />
            <PasswordTextField
                name="confirmPassword"
                label={t('auth:confirmPassword')}
                value={confirmPassword}
                showPassword={showConfirmPassword}
                onChange={onConfirmPasswordChange}
                onToggleVisibility={onToggleConfirmPasswordVisibility}
                showLabel={t('auth:showPassword')}
                hideLabel={t('auth:hidePassword')}
            />
            {onInviteKeyChange && (
                <TextField
                    margin="normal"
                    required={inviteKeyRequired}
                    fullWidth
                    variant="outlined"
                    id="inviteKey"
                    label={t('auth:inviteKey')}
                    name="inviteKey"
                    placeholder={t('auth:inviteKeyPlaceholder')}
                    value={inviteKey ?? ''}
                    onChange={onInviteKeyChange}
                    error={inviteKeyHasError}
                    aria-invalid={inviteKeyHasError}
                    inputRef={inviteKeyInputRef}
                    helperText={
                        inviteKeyHasError
                            ? inviteKeyError
                            : inviteKeyRequired
                              ? t('auth:inviteKeyHelperRequired')
                              : t('auth:inviteKeyHelper')
                    }
                    slotProps={{
                        htmlInput: {
                            'aria-describedby': inviteKeyHasError
                                ? 'inviteKey-validation-error'
                                : undefined,
                        },

                        formHelperText: {
                            id: inviteKeyHasError ? 'inviteKey-validation-error' : undefined,
                        },
                    }}
                />
            )}
        </>
    )
}
